import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';
import { YesNoMessageboxComponent } from '@components/_base/_messageboxes/yes-no-messagebox/yes-no-messagebox.component';
import { modalMinWidth } from '@env/config';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { UtilValidators } from '@app/_validators/util.validators';

import { BlacklistService } from '../_services/blacklist.service';
import { Blacklist } from '../_models/blacklist.model';

@Component({
  selector: 'app-add-edit-blacklist',
  templateUrl: './add-edit-blacklist.component.html',
  styleUrls: ['./add-edit-blacklist.component.css']
})
export class AddEditBlacklistComponent extends BaseParentComponent implements OnInit {

  disableActionBtn = false;
  dataLoaded = false;

  mainForm = this.fb.group({
    id:           [null],
    smsChannel:   [false],
    emailChannel: [false],
    phone:        [null],
    email:        [null],
    reason:       [null, Validators.required],
    note:         [null],
    createdOn:    [null]
  }, { validators: this.atLeastOneChannelValidator });

  constructor(
    private router:            Router,
    private route:             ActivatedRoute,
    private fb:                FormBuilder,
    private dialog:            MatDialog,
    private uiEvent:           UIEventCustom,
    private toastr:            ToastrService,
    private translateService:  TranslateService,
    private blacklistService:  BlacklistService,
  ) {
    super(router);
  }

  ngOnInit() {
    this.showValidationsSubscription = this.showValidations$.subscribe({
      next: (data) => this.showValidations = data
    });

    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.blacklistService.findById(+id).subscribe({
          next: (entry) => {
            this.mainForm.patchValue({
              id:           entry.id,
              smsChannel:   !!entry.smsChannel,
              emailChannel: !!entry.emailChannel,
              phone:        entry.phone,
              email:        entry.email,
              reason:       entry.reason,
              note:         entry.note,
              createdOn:    entry.createdOn
            });
            this.applyChannelValidators();
            this.dataLoaded = true;
          },
          error: (err) => {
            displayError(err);
            this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          }
        });
      } else {
        this.applyChannelValidators();
        this.dataLoaded = true;
      }
    });
  }

  get f() {
    return this.mainForm.controls;
  }

  get isEdit(): boolean {
    return !!this.mainForm.value.id;
  }

  atLeastOneChannelValidator(group: AbstractControl): ValidationErrors | null {
    const sms = !!group.get('smsChannel')?.value;
    const email = !!group.get('emailChannel')?.value;
    return sms || email ? null : { atLeastOneChannel: true };
  }

  onSmsChannelChange() {
    this.applyChannelValidators();
  }

  onEmailChannelChange() {
    this.applyChannelValidators();
  }

  private applyChannelValidators() {
    const phoneCtrl = this.mainForm.get('phone');
    const emailCtrl = this.mainForm.get('email');

    if (this.f.smsChannel.value) {
      phoneCtrl?.setValidators([Validators.required, UtilValidators.validatePhoneNumber()]);
    } else {
      phoneCtrl?.clearValidators();
    }
    phoneCtrl?.updateValueAndValidity();

    if (this.f.emailChannel.value) {
      emailCtrl?.setValidators([Validators.required, Validators.email]);
    } else {
      emailCtrl?.clearValidators();
    }
    emailCtrl?.updateValueAndValidity();
  }

  save() {
    if (!this.mainForm.valid) {
      this.uiEvent.displayUIError();
      this.showValidationsSubject.next(true);
      return;
    }

    const formValue = this.mainForm.value;
    const payload: Blacklist = {
      id:           formValue.id ?? null,
      smsChannel:   !!formValue.smsChannel,
      emailChannel: !!formValue.emailChannel,
      phone:        formValue.smsChannel ? formValue.phone : null,
      email:        formValue.emailChannel ? formValue.email : null,
      reason:       formValue.reason,
      note:         formValue.note,
      createdOn:    formValue.createdOn
    };

    this.disableActionBtn = true;
    this.blacklistService.save(payload).subscribe({
      next: () => {
        this.uiEvent.displayUISuccess();
        this.disableActionBtn = false;
        this.router.navigate(['/list-blacklists']);
      },
      error: err => {
        this.disableActionBtn = false;
        this.handleSaveError(err);
      }
    });
  }

  delete() {
    if (!this.isEdit) return;
    const id = this.mainForm.value.id;

    const dialogRef = this.dialog.open(YesNoMessageboxComponent, {
      ...modalMinWidth,
      data: {
        isWarning: true,
        message: 'blacklist.deleteConfirm'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.disableActionBtn = true;
      this.blacklistService.delete(id).subscribe({
        next: () => {
          this.uiEvent.displayUISuccess();
          this.disableActionBtn = false;
          this.router.navigate(['/list-blacklists']);
        },
        error: err => {
          this.disableActionBtn = false;
          displayError(err);
          this.uiEvent.displayUIError();
        }
      });
    });
  }

  private handleSaveError(err: any) {
    displayError(err);
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) : null;
    if (translated && translated !== i18nKey) {
      this.toastr.error(translated, this.translateService.instant('toast.errorTitle'));
    } else {
      this.uiEvent.displayUIError();
    }
  }
}
